<template>
    <div>
        <div class="d-flex mb-3">
            <pc-button
                v-if="showGoToParent"
                class="me-3"
                v-on:click="openParentFolder">
                {{ goBackButton }}
            </pc-button>
            <div class="mt-2 mb-2 fw-bold">
                {{ folderName }}
            </div>
        </div>
        <library-folder
            v-bind:folder-name="folderName"
            v-bind:items="items"
            v-bind:unavailable-item-ids="unavailableItemIds"
            v-bind:existing-selected-item-id="selectedItemId"
            selector-mode
            v-on:open-folder="openChildFolder"
            v-on:select-file="selectFile" />
        <div class="fst-italic">{{ selectItemLabel }} {{ selectedItemName }}</div>
    </div>
</template>
<script>
    import libraryFolder from "ContentLibrary/libraryFolder.vue";
    import { DIALOG_RESULT, DIALOG_HOOK } from "Components/pc-dialog.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/addContentItemDialog.i18n";

    const defaultIds = {
        root: "ROOT",
        public: "PUBLIC",
        program: "PROGRAM"
    };
    const rootFolder = {
        Name: "Content Library",
        Id: defaultIds.root
    };
    const rootItems = [
        {
            Name: "Public Library",
            Id: defaultIds.public,
            FileType: 0,
            ParentId: defaultIds.root
        },
        {
            Name: "Program Library",
            Id: defaultIds.program,
            FileType: 0,
            ParentId: defaultIds.root
        }
    ];

    export default {
        components: {
            libraryFolder
        },
        props: {
            allItems: { type: Array, required: true },

            /**
             * Optional array of item IDs. Can be specified to indicate that items
             * exist but cannot be selected
             */
            unavailableItemIds: { type: Array, required: false, default: () => [] },
            existingSelectedLibraryItem: { type: Object, required: false, default: null }
        },
        emits: ["item-selected"],
        data() {
            return {
                folderLoading: false,
                currentFolder: rootFolder,
                parentFolder: null,
                items: rootItems,
                selectedItem: this.existingSelectedLibraryItem
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            folderName() {
                return this.currentFolder.Name;
            },
            showGoToParent() {
                return this.currentFolder.Id != defaultIds.root;
            },
            selectedItemName() {
                if (!this.selectedItem) {
                    return this.selectedItemNoneText;
                } else if (this.selectedItem.IsPublic) {
                    return `${this.publicLibraryLabel}/${this.selectedItem.ContentPath}`;
                } else {
                    return `${this.programLibraryLabel}/${this.selectedItem.ContentPath}`;
                }
            },
            selectedItemId() {
                if (!this.selectedItem) {
                    return null;
                } else {
                    return this.selectedItem.Id;
                }
            }
        },
        created() {
            this.setLocalizedNames();
        },
        methods: {
            setLocalizedNames() {
                rootFolder.Name = this.contentLibraryLabel;
                rootItems.find((ri) => ri.Id == defaultIds.public).Name = this.publicLibraryLabel;
                rootItems.find((ri) => ri.Id == defaultIds.program).Name = this.programLibraryLabel;
            },
            async openChildFolder(folder) {
                await this.$nextTick();
                this.folderLoading = true;
                this.parentFolder = this.currentFolder;
                this.currentFolder = folder;
                this.loadCurrentItems();
                this.folderLoading = false;
            },
            findParentFolder() {
                const parentId = this.currentFolder.ParentId;
                if (!parentId) {
                    // currentFolder is rootFolder
                    return null;
                }
                let parentFolder = this.allItems.find((i) => i.Id === parentId);
                if (parentFolder) {
                    // parent folder is a folder item
                    return parentFolder;
                }
                parentFolder = rootItems.find((ri) => ri.Id == parentId);
                if (parentFolder) {
                    // parent folder is a root item
                    return parentFolder;
                }
                if (rootItems.some((ri) => ri.Id == this.currentFolder.Id)) {
                    // parent folder is rootFolder
                    return rootFolder;
                }
                throw this.invalidProgramIdMessage;
            },
            openParentFolder() {
                if (!this.parentFolder) {
                    return;
                }
                this.folderLoading = true;
                this.currentFolder = this.parentFolder;
                this.parentFolder = this.findParentFolder();
                this.$nextTick(() => {
                    this.loadCurrentItems();
                    this.folderLoading = false;
                });
            },
            loadCurrentItems() {
                if (this.currentFolder.Id === defaultIds.root) {
                    this.items = rootItems;
                } else {
                    this.items = this.allItems.filter((i) => i.ParentId === this.currentFolder.Id);
                }
            },
            selectFile(file) {
                this.selectedItem = file;
                this.$emit("item-selected", this.selectedItem);
            },
            [DIALOG_HOOK.getResult]() {
                return this.selectedItem?.Id;
            },
            [DIALOG_HOOK.isValidToClose](result) {
                if (result === DIALOG_RESULT.cancel) {
                    return true;
                }
                return Boolean(this.selectedItem);
            }
        }
    };
</script>
